import type { ForwardedRef, HTMLProps, ReactNode } from 'react';
import { forwardRef, useEffect, useState } from 'react';

import { cn } from '~/lib/cn';
import { variants } from '~/lib/variants';

import { Tag } from '../Tag';

interface RadioGroupOption {
  value: string;
  label: ReactNode;
}

type RadioGroupVariant = 'tag' | 'radio';

const radioGroup = variants({
  slots: {
    tag: cn('cursor-pointer'),
    input: cn(
      'relative h-md w-md cursor-pointer appearance-none rounded-full border transition-colors border-default',
      "after:absolute after:inset-[4px] after:scale-0 after:rounded-full after:transition-transform after:content-['']",
      'checked:border-secondary-default checked:after:scale-100 checked:after:bg-secondary-default',
    ),
  },
  variants: {
    variant: {
      tag: {
        input: cn('sr-only'),
      },
      radio: {
        tag: cn(
          'outline-0 duration-0 bg-transparent text-strong hover:outline-none hover:outline-0 hover:bg-transparent',
        ),
      },
    },
  },
});

export type RadioGroupProps = {
  options: RadioGroupOption[];
  variant?: RadioGroupVariant;
} & HTMLProps<HTMLInputElement>;

export const RadioGroup = forwardRef(
  (
    {
      options,
      name,
      className,
      value,
      disabled,
      readOnly,
      variant = 'tag',
      ...inputProps
    }: RadioGroupProps,
    ref: ForwardedRef<HTMLInputElement>,
  ) => {
    // Used only for styling, value is controlled by the input or RHF
    // This is so that the component can be used in controlled and uncontrolled forms
    const [valueForStyling, setValueForStyling] = useState<string>();

    const { tag, input } = radioGroup({ variant });

    useEffect(() => {
      setValueForStyling(value ? value.toString() : undefined);
    }, [value]);

    return (
      <fieldset className={cn('flex', 'flex-wrap', 'gap-sm', className)}>
        <legend className="sr-only">{name}</legend>
        {options.map((option) => (
          <label key={option.value}>
            <Tag
              variant={
                valueForStyling === option.value ? 'active' : 'secondary'
              }
              className={tag()}
              disabled={disabled || readOnly}
            >
              <input
                ref={ref}
                type="radio"
                name={name}
                {...inputProps}
                checked={value === option.value}
                value={option.value}
                disabled={disabled}
                readOnly={readOnly}
                onChange={(e) => {
                  if (disabled || readOnly) {
                    e.preventDefault();
                    e.stopPropagation();

                    return;
                  }

                  inputProps.onChange?.(e);
                  setValueForStyling(option.value);
                }}
                className={input()}
              />
              {option.label}
            </Tag>
          </label>
        ))}
      </fieldset>
    );
  },
);

RadioGroup.displayName = 'RadioGroup';
